import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Sparkles, Ruler, Star } from "lucide-react";
import { getProducts, getCollections } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { useStore } from "@/context/StoreContext";
import { REVIEWS } from "@/lib/reviews";

const HERO_IMG = "https://customer-assets.emergentagent.com/job_fashion-ai-98/artifacts/7it0wv95_9E6DE302-A2DC-44D4-8CF3-93F5FF0EB8FF.png";

const SectionHead = ({ overline, bold, italic, to }) => (
  <div className="flex items-end justify-between mb-8">
    <div>
      {overline && <p className="overline text-black/40">{overline}</p>}
      <h2 className="font-hero text-5xl sm:text-6xl mt-2">{bold} {italic && <span className="italic font-display font-light lowercase tracking-tight">{italic}</span>}</h2>
    </div>
    {to && <Link to={to} className="overline link-underline hidden sm:block whitespace-nowrap">View All →</Link>}
  </div>
);

const Row = ({ products }) => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-10">
    {products.slice(0, 4).map((p, i) => <ProductCard key={p.handle} product={p} index={i} />)}
  </div>
);

export default function Home() {
  const { setStylistOpen, setSizeFinderOpen, loyalty } = useStore();
  const { data: newProducts = [] } = useQuery({ queryKey: ["new"], queryFn: () => getProducts({ collection: "new" }) });
  const { data: tees = [] } = useQuery({ queryKey: ["tees"], queryFn: () => getProducts({ collection: "tees" }) });
  const { data: shorts = [] } = useQuery({ queryKey: ["shorts"], queryFn: () => getProducts({ collection: "shorts" }) });
  const { data: sets = [] } = useQuery({ queryKey: ["sets"], queryFn: () => getProducts({ collection: "sets" }) });
  const { data: headwear = [] } = useQuery({ queryKey: ["headwear"], queryFn: () => getProducts({ collection: "headwear" }) });
  const { data: collections = [] } = useQuery({ queryKey: ["collections"], queryFn: getCollections });
  const rotationImg = newProducts.find((p) => p.images?.length)?.images?.[0] || HERO_IMG;

  return (
    <div>
      {/* HERO — full-bleed cinematic, bottom-left anchored (KZA) */}
      <section className="relative h-[88vh] min-h-[560px] w-full overflow-hidden bg-[#0A0A0A]">
        <img src={HERO_IMG} alt="LÉVINCHI WORLD" className="absolute inset-0 w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-black/5" />
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    className="relative z-10 h-full flex flex-col items-start justify-end text-left text-[#F4F2ED] px-5 sm:px-8 lg:px-12 pb-14 sm:pb-20 max-w-5xl">
          <p className="overline text-white/80 mb-4 drop-shadow-lg">Summer '26 Capsule · Now Live</p>
          <h1 className="font-hero uppercase text-6xl sm:text-8xl lg:text-[8.5rem] leading-[0.8] [text-shadow:0_4px_40px_rgba(0,0,0,0.55)]">LÉVINCHI<br />WORLD</h1>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/collections/new" data-testid="hero-shop-drop-button" className="btn-fill bg-[#0A0A0A] text-white border border-white/10 hover:bg-neutral-800">Shop the Drop <ArrowRight size={16} /></Link>
            <button onClick={() => setStylistOpen(true)} data-testid="hero-stylist-btn" className="btn-ghost-light"><Sparkles size={16} /> Ask the Stylist</button>
          </div>
        </motion.div>
      </section>

      {/* NEW ARRIVALS */}
      <section className="px-5 sm:px-8 lg:px-12 py-20 sm:py-24">
        <SectionHead overline="Just Dropped" bold="NEW" italic="arrivals" to="/collections/new" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-10">
          {newProducts.filter((p) => p.available).map((p, i) => <ProductCard key={p.handle} product={p} index={i} />)}
        </div>
      </section>

      {/* TEES */}
      {tees.length > 0 && (
        <section className="px-5 sm:px-8 lg:px-12 pb-20 sm:pb-24">
          <SectionHead overline="The Signature" bold="TEES" to="/collections/tees" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-10">
            {tees.filter((p) => p.available).map((p, i) => <ProductCard key={p.handle} product={p} index={i} />)}
          </div>
        </section>
      )}

      {/* SHORTS */}
      {shorts.filter((p) => p.available).length > 0 && (
        <section className="px-5 sm:px-8 lg:px-12 pb-20 sm:pb-24">
          <SectionHead overline="LÉVINCHI World" bold="SHORTS" to="/collections/shorts" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-10">
            {shorts.filter((p) => p.available).map((p, i) => <ProductCard key={p.handle} product={p} index={i} />)}
          </div>
        </section>
      )}

      {/* SETS */}
      {sets.filter((p) => p.available && !p.collections.includes("new")).length > 0 && (
        <section className="relative bg-[#0A0A0A] text-[#F4F2ED]">
          <div className="px-5 sm:px-8 lg:px-12 py-20 sm:py-24">
            <div className="flex items-end justify-between mb-8">
              <div>
                <p className="overline text-white/40">Two-Piece</p>
                <h2 className="font-hero text-5xl sm:text-6xl mt-2">SETS</h2>
              </div>
              <Link to="/collections/sets" className="overline link-underline hidden sm:block">View All →</Link>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-10">
              {sets.filter((p) => p.available && !p.collections.includes("new")).map((p, i) => <ProductCard key={p.handle} product={p} index={i} />)}
            </div>
          </div>
        </section>
      )}

      {/* HEADWEAR */}
      {headwear.length > 0 && (
        <section className="px-5 sm:px-8 lg:px-12 py-20 sm:py-24">
          <SectionHead overline="Top It Off" bold="HEADWEAR" to="/collections/headwear" />
          <Row products={headwear.filter((p) => p.available)} />
        </section>
      )}

      {/* THE PROCESS — brand narrative */}
      <section data-testid="the-process-section" className="bg-[#F4F2ED] border-y border-black/10">
        <div className="px-5 sm:px-8 lg:px-12 py-20 sm:py-28 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <p className="overline text-black/40">The Process</p>
            <h2 className="font-hero text-5xl sm:text-6xl lg:text-7xl uppercase leading-[0.85] mt-3">Every piece<br />starts as a<br />drawing.</h2>
          </div>
          <p className="font-body text-black/70 text-base sm:text-lg leading-relaxed max-w-xl">
            Every graphic is drawn by hand in the studio. From there, each piece is cut and sewn in small runs — no mass program, no mall-tier compromises. The hand-finished detail is the only tell. This is LÉVINCHI WORLD.
          </p>
        </div>
      </section>

      {/* IN ROTATION — worn by artists */}
      <section data-testid="in-rotation-section" className="relative bg-[#0A0A0A] text-[#F4F2ED] overflow-hidden">
        <div className="grid lg:grid-cols-2">
          <div className="relative aspect-[4/5] lg:aspect-auto lg:min-h-[600px] overflow-hidden">
            <img src={rotationImg} alt="Worn by LÉVINCHI" className="absolute inset-0 w-full h-full object-cover object-center" />
          </div>
          <div className="flex flex-col justify-center px-5 sm:px-8 lg:px-16 py-16 sm:py-24">
            <p className="overline text-white/40">In Rotation</p>
            <h2 className="font-hero text-5xl sm:text-6xl lg:text-7xl uppercase leading-[0.85] mt-3">Worn by<br />the ones<br />who know</h2>
            <p className="font-body text-white/60 mt-6 max-w-md leading-relaxed">
              Seen on the culture — from the stage to the streets. The fits that move with the ones setting the pace. Cop the look before the next drop lands.
            </p>
            <Link to="/collections/new" data-testid="in-rotation-cta" className="btn-ghost-light mt-8 self-start">Shop the Fits <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="relative bg-[#0A0A0A] text-[#F4F2ED]">
        <div className="px-5 sm:px-8 lg:px-12 py-20 sm:py-24">
          <div className="text-center mb-12">
            <div className="flex justify-center gap-1 mb-3">{Array.from({ length: 5 }).map((_, i) => <Star key={i} size={16} className="fill-[#F4F2ED] text-[#F4F2ED]" />)}</div>
            <h2 className="font-hero text-5xl sm:text-6xl">LOVED BY THE FAM</h2>
            <p className="font-body text-white/50 mt-3">Real words from real customers.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {REVIEWS.slice(0, 6).map((r, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 3) * 0.08 }}
                          className="border border-white/10 p-6" data-testid={`review-card-${i}`}>
                <div className="flex gap-0.5 mb-3">{Array.from({ length: r.stars }).map((_, j) => <Star key={j} size={13} className="fill-[#F4F2ED] text-[#F4F2ED]" />)}</div>
                <p className="font-body text-sm text-white/80 leading-relaxed">"{r.text}"</p>
                <p className="overline mt-4 text-white/50">{r.name}{r.verified && " · Verified"}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* STOCKISTS — find it in person */}
      <section data-testid="stockists-section" className="bg-[#EDEDED] border-b border-black/10">
        <div className="px-5 sm:px-8 lg:px-12 py-20 sm:py-24">
          <div className="max-w-2xl mb-12">
            <p className="overline text-black/40">Find It In Person</p>
            <h2 className="font-hero text-5xl sm:text-6xl uppercase leading-none mt-3">Stockist Boutiques</h2>
            <p className="font-body text-black/60 mt-4">Stocked at a curated list of boutique partners across key cities. Walk in and ask for the latest drop.</p>
          </div>
          <div data-testid="stockists-grid" className="grid grid-cols-2 lg:grid-cols-4 border-t border-l border-black/15">
            {[
              { name: "TOKEN", city: "MIAMI, FL" },
              { name: "STASH", city: "HOUSTON, TX" },
              { name: "COURTSIDE", city: "RALEIGH, NC" },
              { name: "LÉVINCHI STUDIO", city: "ATLANTA, GA" },
            ].map((s) => (
              <div key={s.name} className="border-r border-b border-black/15 p-6 sm:p-8" data-testid={`stockist-${s.name.toLowerCase().replace(/\s+/g, "-")}`}>
                <p className="font-hero text-2xl sm:text-3xl uppercase">{s.name}</p>
                <p className="overline text-black/40 mt-2">{s.city}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI STYLIST + SIZE FINDER */}
      <section className="px-5 sm:px-8 lg:px-12 py-20 sm:py-24 grid md:grid-cols-2 gap-4">
        <div className="bg-[#0A0A0A] text-[#F4F2ED] p-10 sm:p-12 flex flex-col justify-between min-h-[280px]">
          <div>
            <Sparkles size={26} strokeWidth={1.25} />
            <h3 className="font-hero text-4xl mt-5">YOUR AI STYLIST</h3>
            <p className="font-body text-white/60 mt-3 max-w-sm">Powered by GPT-4o. Describe the occasion — get a complete fit from our live drops in seconds.</p>
          </div>
          <button onClick={() => setStylistOpen(true)} data-testid="band-stylist-btn" className="btn-ghost-light mt-6 self-start">Start Styling</button>
        </div>
        <div className="bg-[#EDEDED] p-10 sm:p-12 flex flex-col justify-between min-h-[280px]">
          <div>
            <Ruler size={26} strokeWidth={1.25} />
            <h3 className="font-hero text-4xl mt-5">NAIL YOUR FIT</h3>
            <p className="font-body text-black/60 mt-3 max-w-sm">Our Smart Size Finder reads your measurements so you order right — and never return.</p>
          </div>
          <button onClick={() => setSizeFinderOpen(true)} data-testid="band-sizefinder-btn" className="btn-outline mt-6 self-start">Find My Size</button>
        </div>
      </section>

      {/* LOYALTY */}
      <section className="bg-[#0A0A0A] text-[#F4F2ED]">
        <div className="px-5 sm:px-8 lg:px-12 py-24 sm:py-28 text-center max-w-3xl mx-auto">
          <p className="overline text-white/50">LEVINCHI Rewards</p>
          <h2 className="font-hero text-5xl sm:text-7xl mt-4 leading-none">EARN AS YOU<br />WEAR THE WORLD</h2>
          <p className="font-body text-white/60 mt-6 text-lg">Points on every move. Tiers that unlock early drops and VIP access. {loyalty ? `You've got ${loyalty.points} points as a ${loyalty.tier.name}.` : "Start earning today."}</p>
          <Link to="/rewards" data-testid="loyalty-cta-btn" className="btn-fill bg-[#F4F2ED] text-[#0A0A0A] hover:bg-white mt-8 inline-flex">See Your Rewards <ArrowRight size={16} /></Link>
        </div>
      </section>
    </div>
  );
}
